import { invariantResponse } from "@epic-web/invariant"
import {
	type LoaderFunctionArgs,
	json,
	type ActionFunctionArgs,
	redirect,
} from "@remix-run/node"
import { Link, useFetcher, useLoaderData } from "@remix-run/react"
import ItemsTable from "#app/components/products/items-table"
import { Button } from "#app/components/ui/button"
import {
	Card,
	CardContent,
	CardDescription,
	CardFooter,
	CardHeader,
	CardTitle,
} from "#app/components/ui/card"
import { Icon } from "#app/components/ui/icon"
import { Separator } from "#app/components/ui/separator"
import {
	TypographyH1,
	TypographyLarge,
	TypographyMuted,
	TypographyP,
	TypographySmall,
} from "#app/components/ui/typography"
import { requireUserId } from "#app/utils/auth.server"
import {
	calcPrices as recalculateCartPricing,
	getCartSessionId,
	removeCartItem,
	getCart,
} from "#app/utils/cart.server"
import { APP_NAME } from "#app/utils/constants.js"
import { prisma } from "#app/utils/db.server.ts"
import { generateMeta } from "#app/utils/meta.js"
import { cn, price } from "#app/utils/misc"
import { createCheckoutSession } from "#app/utils/stripe.server"
import {
	createToastHeaders,
	redirectWithToast,
	type ToastInput,
} from "#app/utils/toast.server"
import { useOptionalUser } from "#app/utils/user"
import routes from "#routes"

export const meta = generateMeta({
	title: `Carrito - ${APP_NAME}`,
	description:
		"Revisa tu carrito y finaliza tu compra. Echa un último vistazo a tus productos seleccionados, verifica los detalles y completa tu compra de forma segura en Greta's Patchwork. ¡No te pierdas tus artículos favoritos!",
	canonical: routes.public.cart.route(),
})

export const loader = async ({ request }: LoaderFunctionArgs) => {
	const { sessionId } = await getCartSessionId(request)

	const cart = await prisma.cart.findUnique({
		where: {
			sessionId,
		},
		select: {
			shippingPrice: true,
			totalPrice: true,
			itemsPrice: true,
			items: {
				select: {
					id: true,
					quantity: true,
					productVariantId: true,
					product: {
						select: {
							name: true,
							id: true,
							price: true,
							handle: true,
							images: {
								take: 1,
							},
							variants: {
								select: {
									id: true,
									price: true,
									title: true,
								},
							},
						},
					},
				},
			},
		},
	})

	invariantResponse(cart, "Not found", {
		status: 404,
	})

	return json({ cart })
}

export const addToCartIntent = "add-to-cart"
export const updateCartItemIntent = "update-cart-item"
export const removeFromCartIntent = "remove-from-cart"
const proceedToCheckoutIntent = "proceed-to-checkout"

export const action = async ({ request }: ActionFunctionArgs) => {
	const formData = await request.formData()
	const intent = formData.get("intent")
	const { sessionId } = await getCartSessionId(request)

	const cart = await prisma.cart.findUnique({
		where: {
			sessionId,
		},
		include: {
			items: {
				include: {
					product: {
						include: {
							images: {
								take: 1,
							},
						},
					},
				},
			},
		},
	})
	let toast: ToastInput | undefined = undefined

	switch (intent) {
		case addToCartIntent: {
			const productId = formData.get("productId") as string
			const variantId = formData.get("variantId") as string | undefined

			invariantResponse(productId, "No productId found.", {
				status: 400,
			})
			invariantResponse(cart, "No cart found with that session id.", {
				status: 500,
			})

			const cartItemId = cart?.items.find(item => {
				if (variantId)
					return (
						item.productId === productId && item.productVariantId === variantId
					)

				return item.productId === productId
			})?.id

			// The product exists, update it
			if (cartItemId)
				await prisma.cartItem.update({
					where: {
						id: cartItemId,
					},
					data: {
						quantity: {
							increment: 1,
						},
					},
				})
			else {
				await prisma.cartItem.create({
					data: {
						cartId: cart?.id,
						productId,
						quantity: 1,
						productVariantId: variantId,
					},
				})
			}

			toast = {
				description: "Producto añadido al carrito.",
				type: "success",
			}

			break
		}
		case updateCartItemIntent: {
			break
		}
		case removeFromCartIntent: {
			const cartItemId = formData.get("cartItemId") as string
			invariantResponse(cartItemId, "No cartItemId found.", {
				status: 400,
			})

			await removeCartItem(cartItemId)

			toast = {
				description: "Producto eliminado del carrito.",
				type: "success",
			}
			break
		}
		case proceedToCheckoutIntent: {
			invariantResponse(cart, "No cart found with that session id.", {
				status: 500,
			})

			// If some item in the cart has more occurences than there are in stock, throw error.
			if (cart.items.some(item => item.quantity > item.product.stock)) {
				return json(
					{ success: false },
					{
						headers: await createToastHeaders({
							title: "Ups!",
							description:
								"Alguno de los productos de tu carrito no está disponible.",
							type: "error",
						}),
					},
				)
			}

			await requireUserId(request, {
				redirectTo: routes.public.cart.route(),
			})

			const checkoutSessionUrl = await createCheckoutSession(request)

			await prisma.cart.update({
				where: {
					id: cart.id,
				},
				data: {
					checkoutId: checkoutSessionUrl.id,
				},
			})

			return redirect(checkoutSessionUrl.url!, 303)
		}
	}

	const newCart = await getCart(request)
	await recalculateCartPricing(newCart)

	return toast?.description
		? redirectWithToast(routes.public.cart.route(), {
				...toast,
			})
		: redirect(routes.public.cart.route())
}

export default function CartPage() {
	const fetcher = useFetcher()
	const user = useOptionalUser()
	const { cart } = useLoaderData<typeof loader>()

	return (
		<div className="container flex flex-col gap-6">
			<TypographyH1>Carrito</TypographyH1>
			<div className="grid items-start gap-4 md:grid-cols-[66%_33%]">
				<Card>
					<CardHeader>
						<CardTitle>Artículos</CardTitle>
						<CardDescription>
							{cart.items.length === 0
								? "Los artículos que añadas al carrito aparecerán aquí."
								: "Aquí aparecen los artículos que has añadido al carrito."}
						</CardDescription>
					</CardHeader>

					<CardContent className="flex flex-col gap-6">
						{cart.items.length === 0 ? (
							<div>
								<TypographyP>Tu carrito está vacío.</TypographyP>
							</div>
						) : (
							<ItemsTable
								// @ts-ignore
								items={cart.items}
								canDelete
							/>
						)}
					</CardContent>
					{cart.items.length === 0 && (
						<CardFooter>
							<Link to={routes.public.products.route()}>
								<Button variant="secondary">Volver a productos</Button>
							</Link>
						</CardFooter>
					)}
				</Card>

				<Card>
					<CardHeader>
						<CardTitle>Precio</CardTitle>
						<CardDescription>
							{cart.items.length}{" "}
							{cart.items.length === 1 ? "artículo" : "artículos"}
						</CardDescription>
					</CardHeader>
					<CardContent className="flex flex-col gap-4">
						{cart.items.length > 0 && (
							<div>
								<div className="flex items-end justify-between gap-2">
									<TypographyMuted>Subtotal</TypographyMuted>
									<TypographyP>
										{cart.itemsPrice ? price(cart.itemsPrice) : "-"}
									</TypographyP>
								</div>
								<div className="flex items-end justify-between gap-2">
									<TypographyMuted>Envío</TypographyMuted>
									<TypographyP>
										{cart.shippingPrice ? price(cart.shippingPrice) : "-"}
									</TypographyP>
								</div>
								{cart.shippingPrice !== 0 && (
									<div className="mt-2 rounded bg-muted px-3 pb-3 pt-2.5">
										<TypographySmall className="">
											Para pedidos superiores a 100€ el envío es gratuito.
										</TypographySmall>
									</div>
								)}
								<div className="flex items-end justify-between gap-2">
									<TypographyMuted>IVA</TypographyMuted>
									<TypographyP>Incluido</TypographyP>
								</div>
							</div>
						)}
						<Separator />

						<div className="flex items-end justify-between gap-2">
							<TypographyMuted>Total</TypographyMuted>
							<TypographyLarge className="text-2xl font-semibold">
								{cart.totalPrice ? price(cart.totalPrice) : "-"}
							</TypographyLarge>
						</div>
					</CardContent>
					<CardFooter>
						{/* <ProceedToCheckoutButton
							disabled={cart.items.length === 0}
							onClick={() => {}}
						/> */}
						<fetcher.Form method="POST" className="w-full">
							<Button
								type="submit"
								name="intent"
								value={proceedToCheckoutIntent}
								disabled={cart.items.length === 0}
								className={cn(
									"w-full",
									!user && "border-primary text-primary hover:bg-primary/10",
								)}
								variant={!user ? "outline" : undefined}
							>
								<Icon name={user ? "lucide-dollar-sign" : "lock-open-1"} />
								{user ? "Proceder al pago" : "Iniciar sesión para proceder"}
							</Button>
							{user && cart.items.length > 0 && (
								<div className="mt-2 flex items-start gap-2">
									<div>
										<Icon
											name="lucide-package-check"
											className="text-muted-foreground"
										/>
									</div>
									<TypographyMuted className="mt-1 text-muted-foreground">
										La dirección de envío se establece en la página de pago.
									</TypographyMuted>
								</div>
							)}
						</fetcher.Form>
					</CardFooter>
				</Card>
			</div>
		</div>
	)
}
